import { useEffect, useMemo, useState } from 'react';
import { useDocumentTitle } from 'rooks';
import { useLocation, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Locale } from '@ivario-arc/shared/src/types/locales';
import { ExternalLinks, Paths } from '@src/routes/paths';
import { AnnouncementBar } from '@components/atoms/AnnouncementBar';
import { LandingHeader } from '@components/organisms/LandingHeader';
import { LanguageConfirmOverlay } from '@components/organisms/LanguageConfirmOverlay';
import { useAnnouncement } from '@hooks/useAnnouncement';
import { resolveLanguage, setStoredLanguage } from '@utils/language';

function withReturnTo(target: string, returnTo: string) {
  if (returnTo === '/' || returnTo === '') return target;
  return `${target}?returnTo=${encodeURIComponent(returnTo)}`;
}

const CheckIcon = () => (
  <svg
    viewBox="0 0 12 12"
    className="w-3 h-3 stroke-white stroke-[2.5] fill-none"
  >
    <polyline points="2,6 5,9 10,3" />
  </svg>
);

const CheckIconBlue = () => (
  <svg
    viewBox="0 0 12 12"
    className="w-[11px] h-[11px] stroke-ivarioBlue stroke-[2.5] fill-none"
  >
    <polyline points="2,6 5,9 10,3" />
  </svg>
);

export function LandingView() {
  const { t, i18n } = useTranslation('view', { keyPrefix: 'landing' });
  const { data: announcement } = useAnnouncement();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [showOverlay, setShowOverlay] = useState(false);
  const [detectedLocale, setDetectedLocale] = useState<Locale>(Locale.DE);

  // Homepage-only: set a short, brand-first browser tab title and localize the
  // meta description for the visitor's resolved language. The long SEO title and
  // German description in index.html stay untouched, so they remain what search
  // engines index; only the runtime tab title is decoupled for readability.
  useDocumentTitle(t('tabTitle'));
  const metaDescription = t('metaDescription');
  useEffect(() => {
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute('content', metaDescription);
  }, [metaDescription]);

  const returnTo = useMemo(
    () => location.pathname + location.search,
    [location.pathname, location.search],
  );
  const loginHref = withReturnTo(Paths.LOGIN, returnTo);
  const registerHref = withReturnTo(Paths.REGISTER, returnTo);

  useEffect(() => {
    const { locale, source } = resolveLanguage(
      searchParams,
      i18n.resolvedLanguage,
    );
    setDetectedLocale(locale);

    if (source === 'browser') {
      setShowOverlay(true);
    }

    if (source === 'url' || source === 'storage') {
      setStoredLanguage(locale);
    }

    void i18n.changeLanguage(locale);
    document.documentElement.lang = locale;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const steps = t('newUser.steps', { returnObjects: true }) as string[];
  const infoItems = t('existingUser.info', {
    returnObjects: true,
  }) as string[];

  return (
    <div className="min-h-screen flex flex-col bg-background font-[Outfit]">
      {showOverlay && (
        <LanguageConfirmOverlay
          detectedLocale={detectedLocale}
          onConfirm={() => setShowOverlay(false)}
        />
      )}

      <AnnouncementBar
        section={announcement?.login}
        innerClassName="max-w-[1300px] mx-auto px-4"
      />

      <LandingHeader />

      <main className="flex-1 flex flex-col items-center px-6 py-14 sm:px-6 sm:py-14">
        <h1 className="text-[34px] font-extrabold text-dark text-center tracking-tight leading-tight mb-3">
          {t('pageTitle')}
        </h1>
        <p className="text-base text-gray-600 text-center mb-10 leading-relaxed max-w-[460px]">
          {t('pageSub')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-[860px] w-full items-stretch">
          {/* New User Card */}
          <div className="bg-white rounded-2xl p-7 border-[2.5px] border-ivarioYellow flex flex-col transition-shadow hover:shadow-lg">
            <div className="flex-1 flex flex-col gap-3.5 mb-6">
              <div className="text-xl font-extrabold text-dark">
                {t('newUser.title')}
              </div>
              <p className="text-[15px] text-gray-600 leading-relaxed">
                {t('newUser.description')}
              </p>
              <div className="mt-1">
                <span className="text-sm font-bold text-dark mb-2.5 block">
                  {t('newUser.nextStepsLabel')}
                </span>
                <div className="flex flex-col gap-2">
                  {steps.map((step, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2.5 text-[14.5px] text-gray-600 font-medium"
                    >
                      <div className="w-[22px] h-[22px] min-w-[22px] rounded-full bg-ivarioBlue flex items-center justify-center">
                        <CheckIcon />
                      </div>
                      <span>{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <a
                href={registerHref}
                className="block w-full py-3.5 px-5 bg-ivarioYellow text-dark font-bold text-base rounded-[10px] text-center no-underline transition-all hover:bg-ivarioYellow-hover hover:shadow-md active:scale-[0.98]"
              >
                {t('newUser.cta')}
              </a>
            </div>
          </div>

          {/* Existing User Card */}
          <div className="bg-white rounded-2xl p-7 border-[2.5px] border-ivarioBlue flex flex-col transition-shadow hover:shadow-lg">
            <div className="flex-1 flex flex-col gap-3.5 mb-6">
              <div className="text-xl font-extrabold text-dark">
                {t('existingUser.title')}
              </div>
              <p className="text-[15px] text-gray-600 leading-relaxed">
                {t('existingUser.description')}
              </p>
              <div className="flex flex-col gap-2 mt-1">
                {infoItems.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2.5 text-[14.5px] text-gray-600 font-medium leading-snug"
                  >
                    <div className="w-[22px] h-[22px] min-w-[22px] rounded-full bg-ivarioBlue/10 flex items-center justify-center mt-0.5">
                      <CheckIconBlue />
                    </div>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <a
                href={loginHref}
                className="block w-full py-3.5 px-5 bg-ivarioBlue text-white font-bold text-base rounded-[10px] text-center no-underline transition-all hover:bg-ivarioBlue-hover hover:shadow-md active:scale-[0.98]"
              >
                {t('existingUser.cta')}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center flex flex-col gap-3 max-w-[520px] w-full">
          <a
            href={ExternalLinks.LEGACY_LOGIN}
            className="text-sm text-gray-400 underline underline-offset-4 decoration-gray-300 leading-relaxed hover:text-ivarioBlue hover:decoration-ivarioBlue transition-colors"
          >
            {t('oldResults')}
          </a>
          <p className="text-sm text-gray-400 leading-relaxed">
            {t('helpPrefix')}{' '}
            <a
              href={t('supportUrl')}
              className="text-ivarioBlue no-underline font-semibold hover:underline"
            >
              {t('helpLink')}
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
